import React from 'react';
import { Box, Typography, Stack, Link, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import RecyclingIcon from '@mui/icons-material/Recycling';
import StarIcon from '@mui/icons-material/Star';

const FEATURES = [
  { icon: <RecyclingIcon sx={{ fontSize: 16 }} />, text: '세척마다 탄소 포인트 적립' },
  { icon: <StarIcon sx={{ fontSize: 16 }} />, text: '사용 이력 및 환경 기여도 확인' },
  { icon: <PhoneAndroidIcon sx={{ fontSize: 16 }} />, text: 'QR 스캔으로 1초 세척 시작' },
];

function FirstEventView() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#060d1a',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* 배경 글로우 */}
      <Box
        sx={{
          position: 'absolute',
          width: '60vw',
          height: '60vw',
          maxWidth: '600px',
          maxHeight: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33,150,243,0.18) 0%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 4, md: 8 }}
        sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 8 }, maxWidth: '1100px', width: '100%' }}
      >
        {/* 텍스트 영역 */}
        <Box sx={{ flex: 1, maxWidth: '520px' }}>
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <Chip
              label="다끌레어 앱"
              size="small"
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 700,
                fontSize: '11px',
                letterSpacing: '2px',
                backgroundColor: 'rgba(33,150,243,0.15)',
                color: '#64b5f6',
                border: '1px solid rgba(33,150,243,0.3)',
                mb: 2.5,
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            <Typography
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 900,
                fontSize: { xs: '28px', sm: '36px', md: '46px' },
                color: '#ffffff',
                lineHeight: 1.2,
                letterSpacing: '-1px',
                mb: 2,
              }}
            >
              텀블러 하나로<br />
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(90deg, #4fc3f7, #29b6f6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                환경을 바꿉니다
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Typography
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 300,
                fontSize: { xs: '14px', md: '16px' },
                color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.75,
                mb: 3,
              }}
            >
              다끌레어 앱으로 텀블러 세척기를 이용하고,<br />
              세척할 때마다 탄소 절감 포인트를 적립하세요.
            </Typography>

            {/* 기능 목록 */}
            <Stack spacing={1} sx={{ mb: 4 }}>
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.text}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.55 + i * 0.1 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ color: '#29b6f6', display: 'flex', alignItems: 'center' }}>
                      {f.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: 'NotoSansKR',
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.65)',
                        fontWeight: 400,
                      }}
                    >
                      {f.text}
                    </Typography>
                  </Stack>
                </motion.div>
              ))}
            </Stack>
          </motion.div>

          {/* 앱스토어 배지 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
          >
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Link
                href="https://play.google.com/store"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  transition: 'transform 0.2s, opacity 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', opacity: 0.85 },
                }}
              >
                <img
                  src="/assets/Google-Play-Icon.png"
                  alt="Google Play"
                  style={{ height: '52px', objectFit: 'contain', display: 'block' }}
                />
              </Link>
              <Link
                href="https://www.apple.com/app-store/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  transition: 'transform 0.2s, opacity 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', opacity: 0.85 },
                }}
              >
                <img
                  src="/assets/App-Store-Icon.png"
                  alt="App Store"
                  style={{ height: '52px', objectFit: 'contain', display: 'block' }}
                />
              </Link>
            </Stack>
          </motion.div>
        </Box>

        {/* 오른쪽 앱 아이콘 시각화 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, x: 40 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <Box
            sx={{
              width: { xs: '160px', md: '220px' },
              height: { xs: '160px', md: '220px' },
              borderRadius: '40px',
              background: 'linear-gradient(135deg, #1565c0, #0288d1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 60px rgba(33,150,243,0.35), 0 20px 60px rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
              flexShrink: 0,
            }}
          >
            <Box
              component={motion.div}
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <RecyclingIcon sx={{ fontSize: { xs: '80px', md: '110px' }, color: 'rgba(255,255,255,0.9)' }} />
            </Box>
          </Box>
        </motion.div>
      </Stack>
    </Box>
  );
}

export default FirstEventView;
